import { useCallback, useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { PanelRight, X } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, ON_COLOR, Z_INDEX } from '../../../theme';
import { DialogPortal } from '../../common/DialogPortal';
import { useDesktopShell } from '../../../hooks/useDesktopShell';
import { useSheetTabBarHold } from '../../../hooks/useSheetTabBarHold';
import { CURSOR_POINTER } from '../../../utils/webStyles';
import { PresentationCrossfade } from './PresentationCrossfade';
import { SlideshowControls } from './SlideshowControls';
import { focusMapOnSlide } from './focusMapOnSlide';
import type { Slide } from './deck';
import type { SlideshowController } from './useSlideshowController';

/**
 * The room-sized surface: a modal overlay above every other layer, driven by
 * the same controller as the panel so the deck keeps its position across the
 * jump.
 *
 * This component mounts only while the presentation is in its fullscreen
 * stage, which is what scopes the browser Fullscreen API, the tab-bar hold
 * and the focus handling below — mounted means open, so the effect needs no
 * stage check of its own. Keyboard lives in useSlideshowKeys, one level up,
 * because Escape belongs to both surfaces.
 *
 * Sept 9 2026 client decision: Escape and the close button end the
 * presentation outright now. Dock (desktop shell only) is the one way from
 * here to the panel, so its click has to leave browser fullscreen without
 * tripping the fullscreenchange handler below into ending the presentation
 * too — see the dockingRef in useFullscreenChrome.
 */
export function PresentationFullscreen({ controller }: { controller: SlideshowController }) {
  const { colors } = useTheme();
  const isDesktopShell = useDesktopShell();
  const { current, deck, index, autoplay, exitFullscreen, dock } = controller;
  const progress = deck.length > 0 ? (index + 1) / deck.length : 0;

  // Set the instant Dock is pressed, read by the fullscreenchange handler:
  // dock() leaves browser fullscreen on purpose, and that must not read as
  // the browser-driven exit that ends the presentation.
  const dockingRef = useRef(false);
  const handleDock = useCallback(() => {
    dockingRef.current = true;
    dock();
  }, [dock]);

  useFullscreenChrome(exitFullscreen, dockingRef);
  // React Native's View ref is the underlying DOM node on web, so the
  // HTMLElement cast in the hook is safe — there's no separate web ref type
  // to reach for.
  const containerRef = useFocusOnMount();

  // The map is hidden behind this overlay, so centering has to drop it
  // first — the same exit path Escape uses.
  const handleAddressPress = useCallback(
    (slide: Slide) => {
      exitFullscreen();
      focusMapOnSlide(slide);
    },
    [exitFullscreen]
  );

  return (
    <DialogPortal>
      <View
        ref={containerRef}
        testID="map-slideshow"
        // Raw web a11y attributes — RN Web passes `role` and `aria-*` props
        // straight through (see MapControls' aria-pressed for the same
        // pattern); there's no RN accessibilityRole value for "dialog".
        role="dialog"
        aria-modal
        tabIndex={-1}
        style={[styles.container, { backgroundColor: colors.black }]}>
        <View style={styles.topControls}>
          {isDesktopShell ? (
            <Pressable
              onPress={handleDock}
              testID="map-slideshow-dock"
              accessibilityRole="button"
              accessibilityLabel="Dock to the side"
              style={[styles.chromeButton, { backgroundColor: colors.overlay }, CURSOR_POINTER]}>
              <PanelRight size={22} color={ON_COLOR.text} />
            </Pressable>
          ) : null}
          <Pressable
            onPress={exitFullscreen}
            testID="map-slideshow-close"
            accessibilityRole="button"
            accessibilityLabel="Close presentation"
            style={[styles.chromeButton, { backgroundColor: colors.overlay }, CURSOR_POINTER]}>
            <X size={22} color={ON_COLOR.text} />
          </Pressable>
        </View>

        <PresentationCrossfade
          deck={deck}
          index={index}
          onPrev={controller.prev}
          onNext={controller.next}
          onAddressPress={handleAddressPress}
        />

        {deck.length > 0 ? (
          <View style={styles.progressTrack} pointerEvents="none">
            <View
              testID="map-slideshow-progress"
              style={[
                styles.progressFill,
                {
                  width: `${Math.min(1, Math.max(0, progress)) * 100}%`,
                  backgroundColor: ON_COLOR.text,
                },
              ]}
              // @ts-expect-error rediAnim is a web-only DOM dataset hook
              // picked up by slideshowMotionCss.ts's width transition;
              // ignored on native.
              dataSet={{ rediAnim: 'slideshow-progress' }}
            />
          </View>
        ) : null}

        {current ? (
          <SlideshowControls
            autoplay={autoplay}
            onPrev={controller.prev}
            onNext={controller.next}
            onToggleAutoplay={controller.toggleAutoplay}
            iconColor={ON_COLOR.text}
            style={styles.controls}
          />
        ) : null}

        <Text style={[styles.hint, { color: ON_COLOR.textSecondary }]}>Escape to exit</Text>
      </View>
    </DialogPortal>
  );
}

/**
 * Browser fullscreen plus the map chrome hold, both tied to this surface's
 * mount. `fullscreenchange` is the pair of `requestFullscreen`: leaving
 * fullscreen by any route the browser owns (F11, the Esc the browser eats
 * itself) has to land on the same exit the close button uses. `dockingRef`
 * is the one exception: Dock also leaves browser fullscreen (through the
 * unmount cleanup below), but on purpose, so the resulting event must not
 * end the presentation.
 */
function useFullscreenChrome(onExit: () => void, dockingRef: MutableRefObject<boolean>) {
  const { hold, release } = useSheetTabBarHold();
  useEffect(() => {
    hold();
    // Best-effort: some browsers refuse fullscreen outside a very tight
    // gesture window, and Escape-to-exit is still fine without it.
    document.documentElement.requestFullscreen?.().catch(() => {});
    const onFullscreenChange = () => {
      if (!document.fullscreenElement && !dockingRef.current) onExit();
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      release();
      document.exitFullscreen?.().catch(() => {});
    };
    // Mount/unmount only: re-running this on a controller identity change
    // would exit and re-enter browser fullscreen mid-presentation. dockingRef
    // is a ref, so the closure sees its latest value without needing to be a
    // dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/** Takes focus for the dialog on mount and gives it back on unmount. */
function useFocusOnMount() {
  const ref = useRef<View>(null);
  useEffect(() => {
    const previous = document.activeElement;
    (ref.current as unknown as HTMLElement | null)?.focus?.();
    return () => (previous as HTMLElement | null)?.focus?.();
  }, []);
  return ref;
}

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    zIndex: Z_INDEX.slideshow,
    alignItems: 'center',
    justifyContent: 'center',
  },
  topControls: {
    position: 'absolute',
    top: SPACING.lg,
    right: SPACING.lg,
    flexDirection: 'row',
    gap: SPACING.sm,
    zIndex: 1,
  },
  chromeButton: {
    padding: SPACING.sm,
    borderRadius: BORDER_RADIUS.full,
  },
  controls: {
    paddingBottom: SPACING.lg,
  },
  // The true bottom edge of the screen — a fullscreen overlay with nothing
  // beside it, so animating this is never fighting the map for attention.
  progressTrack: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 2,
  },
  progressFill: {
    height: 2,
  },
  hint: {
    position: 'absolute',
    bottom: SPACING.sm,
    alignSelf: 'center',
    ...TYPOGRAPHY.caption,
  },
});
